<template>
  <div class="">
    <!-- Modal Component -->
    <ModalBox v-model="legacyModalVisible" size="md" hide-close header-class="!hidden" wrapper-classes="lg:h-auto lg:max-h-none p-4">

      <!-- Header -->
      <div class="mb-4">
        <h4 class="text-lg font-semibold text-white mb-1">Transaction</h4>
        <div class="flex items-center gap-2 min-w-0">
          <span class="text-tiny font-medium text-surface-8 shrink-0">ID</span>
          <span class="text-tiny font-mono text-surface-text-1 truncate">{{ txnObject.public_id }}</span>
          <ButtonCopyToClipboard class="shrink-0" :text="txnObject.public_id" />
        </div>
      </div>

      <!-- Details -->
      <div class="rounded-lg bg-surface-1 ring-1 ring-white/[0.04] px-4 divide-y divide-surface-3">
        <div class="flex items-start justify-between gap-4 py-2.5">
          <span class="text-tiny text-surface-8 shrink-0">Content</span>
          <span class="text-sm text-surface-text-1 font-medium text-right">{{ txnObject.product_title || '—' }}</span>
        </div>

        <div class="flex items-center justify-between gap-4 py-2.5">
          <span class="text-tiny text-surface-8 shrink-0">Customer</span>
          <span class="text-sm text-surface-text-1 font-medium text-right truncate">{{ txnObject.user?.email || 'n/a' }}</span>
        </div>

        <div class="flex items-center justify-between gap-4 py-2.5">
          <span class="text-tiny text-surface-8 shrink-0">Payment</span>
          <Badge :type="paymentBadge.type" pill>{{ paymentBadge.label }}</Badge>
        </div>

        <div class="flex items-center justify-between gap-4 py-2.5">
          <span class="text-tiny text-surface-8 shrink-0">Amount</span>
          <span class="text-sm text-surface-text-1 font-semibold text-right">{{ amountDisplay }}</span>
        </div>

        <div class="flex items-center justify-between gap-4 py-2.5">
          <span class="text-tiny text-surface-8 shrink-0">Payment type</span>
          <span class="text-sm text-surface-text-1 font-medium text-right">{{ paymentTypeLabel }}</span>
        </div>

        <div class="flex items-center justify-between gap-4 py-2.5" v-if="paidOn">
          <span class="text-tiny text-surface-8 shrink-0">Paid on</span>
          <span class="text-sm text-surface-text-1 font-medium text-right">{{ paidOn }}</span>
        </div>

        <div class="flex items-center justify-between gap-4 py-2.5" v-if="txnObject.started_at">
          <span class="text-tiny text-surface-8 shrink-0">Access starts</span>
          <span class="text-sm text-surface-text-1 font-medium text-right">{{ formatDate(txnObject.started_at) }}</span>
        </div>

        <div class="flex items-center justify-between gap-4 py-2.5" v-if="txnObject.expires_at">
          <span class="text-tiny text-surface-8 shrink-0">Access ends</span>
          <span class="text-sm text-surface-text-1 font-medium text-right">{{ formatDate(txnObject.expires_at) }}</span>
        </div>

        <div class="flex items-center justify-between gap-4 py-2.5" v-if="txnObject.status">
          <span class="text-tiny text-surface-8 shrink-0">Access</span>
          <Badge :type="accessBadge.type" pill>{{ accessBadge.label }}</Badge>
        </div>
      </div>

      <!-- Footer -->
      <div class="mt-4 flex justify-end" v-if="canRefund">
        <FormButton type="secondary" size="md" :disabled="refundProcessing" @click="refundPayment">
          {{ refundProcessing ? 'Refunding…' : 'Refund' }}
        </FormButton>
      </div>
    
</ModalBox>
  </div>
</template>

<script>
import ModalBox from '@/components/modals/ModalBox.vue'
import { legacyModalBridgeMixin } from '@/composables/useLegacyModalBridge'
import moment from "moment";
import PaywallService from "@/services/PaywallService";
import FormButton from '@/components/Atoms/FormButton.vue';
import Badge from '@/components/Badge.vue';
import ButtonCopyToClipboard from '@/components/Moleculas/ButtonCopyToClipboard.vue';
import { formatMoney } from '@/Utils/money';

export default {
  name: "PaywallTransactionDetailsModal",
  mixins: [legacyModalBridgeMixin],
  components: {
		ModalBox,
    FormButton,
    Badge,
    ButtonCopyToClipboard,
	},
  props: {
    txnObject: {
      type: Object,
      required: true,
    },
  },
  data() {
    return {
      processing: false,
      active: false,
      refundProcessing: false,
    };
  },
  computed: {
    legacyModalId() {
      return 'modal-paywall-transaction-details';
    },
    canRefund() {
      const p = this.txnObject.payments || []
      return p.length > 0 && p[0].status === 'paid'
    },
    paidOn() {
      const p = this.txnObject.payments || []
      return p.length > 0 ? this.formatDate(p[0].dattm) : ''
    },
    amountDisplay() {
      return formatMoney(this.txnObject.price, this.txnObject.currency)
    },
    paymentTypeLabel() {
      if (this.txnObject.recurring) return 'Subscription'
      return Number(this.txnObject.price) === 0 ? 'Free access' : 'Pay-per-view'
    },
    paymentBadge() {
      const s = (this.txnObject.payments || [])[0]?.status
      if (s === 'paid') return { label: 'Paid', type: 'online' }
      if (s === 'refund') return { label: 'Refunded', type: 'error' }
      return { label: s ? this.cap(s) : 'Unpaid', type: 'main' }
    },
    // Invoice status describes whether the viewer currently has access — not a
    // recurring subscription, so it reads as "Access: Active / Expired / …".
    accessBadge() {
      const s = this.txnObject.status
      const map = {
        active: { label: 'Active', type: 'enabled' },
        expired: { label: 'Expired', type: 'main' },
        refund: { label: 'Refunded', type: 'error' },
        chargeback: { label: 'Chargeback', type: 'error' },
        pending: { label: 'Pending', type: 'gold' },
      }
      return map[s] || { label: this.cap(s || ''), type: 'main' }
    },
  },
  mounted() {
  },
  methods: {
    onLegacyModalShow() {
      this.onInit();
    },
    onLegacyModalHide() {
      this.onDismiss();
    },
    cap(s) {
      return s ? String(s).charAt(0).toUpperCase() + String(s).slice(1) : '';
    },
    async refundPayment() {
      this.refundProcessing = true;
      try {
        await PaywallService.refundPayment(this.txnObject.payments[0].id);

        // TODO: emit here without mutation
        this.txnObject.status = "refund";
        this.txnObject.payments[0].status = "refund";
        this.$notify({
          group: "success",
          text: "Payment refunded",
        });
      } catch (err) {
        this.$notify({
          group: "error",
          text: err.message || "refund payment failed",
        });
        console.error("refund payemnet failed", err);
      }
      this.refundProcessing = false;
    },
    onInit() {},
    onDismiss() {},
    onConfirm() {
      this.$emit("modal-confirm");
      this.dismiss();
    },
    dismiss() {
      this.hideLegacyModal();
      // lazy clear
      setTimeout(() => {
        this.processing = false;
      }, 1000);
    },
    formatDate(time) {
      return moment(new Date(time)).format("MMM D, YYYY · h:mm A");
    },
  },
  beforeUnmount() {},
};
</script>

<!-- Add "scoped" attribute to limit CSS to this component only -->
<style scoped>

</style>
